import React, { useContext } from 'react';
import {
  Grid,
  makeStyles,
  createStyles,
  Theme,
  Typography,
  Paper,
} from '@material-ui/core';
import colors from '../../../../themes/main-colors';
import ServiceLocationIcon from '../icons/ServiceLocationIcon';
import HomePinIcon from '../icons/HomePinIcon';
import OutageMarkerIcon from '../icons/OutageMarkerIcon';
import CRCIcon from '../icons/CRCIcon';
import PlannedOutageIcon from '../icons/PlannedOutageIcon';
import AreaIcon from '../icons/AreaIcon';
import { useTranslation } from '../../../../hooks/useTranslation';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import clsx from 'clsx';
import PlannedOutageClusterIcon from '../icons/PlannedOutageClusterIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      gap: '30px',
      padding: theme.spacing(3, 2.5),
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: 20,
    },
    box: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      backgroundColor: colors.white,
      padding: 20,
      flex: 1,
      boxShadow: 'none',
      borderRadius: 0,
    },
    row: {
      gap: '10px',
      '& .MuiGrid-item': {
        display: 'flex',
      },
    },
    label: {
      fontSize: 15,
    },
    mainContainer: {
      gap: '20px',
    },
    icon: {
      width: '33px',
      height: '32px',
    },
    paddingTopBottom: {
      padding: theme.spacing(0.5, 0),
    },
  }),
);

export default function LegendInfo() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isOutagesPSPSEnabled, loading } = useContext(featureFlagsContext);

  return (
    <Grid container direction="column" className={classes.root}>
      <Grid item>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} container direction={'column'}>
            <Typography className={classes.sectionTitle}>
              {t('ACCOUNT')}
            </Typography>
            <Paper className={classes.box}>
              <Grid
                container
                wrap="nowrap"
                alignItems="center"
                className={classes.row}
              >
                <Grid item>
                  <ServiceLocationIcon size={38} />
                </Grid>
                <Grid item>
                  <Typography className={classes.label}>
                    {t('SERVICE_LOCATION')}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                container
                wrap="nowrap"
                alignItems="center"
                className={classes.row}
              >
                <Grid item>
                  <ServiceLocationIcon color={colors.emberRed} size={38} />
                </Grid>
                <Grid item>
                  <Typography className={classes.label}>
                    {t('SERVICE_LOCATION_WITH_OUTAGE')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} container direction={'column'}>
            <Typography className={classes.sectionTitle}>
              {t('SEARCHED_LOCATION')}
            </Typography>
            <Paper className={classes.box}>
              <Grid
                container
                alignItems="center"
                wrap="nowrap"
                className={clsx(classes.row, classes.paddingTopBottom)}
              >
                <Grid item>
                  <HomePinIcon className={classes.icon} />
                </Grid>
                <Grid item>
                  <Typography className={classes.label}>
                    {t('NO_OUTAGE')}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                container
                alignItems="center"
                wrap="nowrap"
                className={clsx(classes.row, classes.paddingTopBottom)}
              >
                <Grid item>
                  <HomePinIcon
                    color={colors.emberRed}
                    className={classes.icon}
                  />
                </Grid>
                <Grid item>
                  <Typography className={classes.label}>
                    {t('HAS_AN_OUTAGE')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* PSPS */}
      {!loading && isOutagesPSPSEnabled && (
        <Grid item>
          <Typography className={classes.sectionTitle}>
            {t('PUBLIC_SAFETY_POWER_SHUTOFF')}
          </Typography>
          <Paper className={classes.box}>
            <Grid
              container
              alignItems="center"
              wrap="nowrap"
              className={classes.row}
            >
              <Grid item>
                <CRCIcon className={classes.icon} />
              </Grid>
              <Grid item>
                <Typography className={classes.label}>
                  {t('COMMUNITY_RESOURCE_CENTER')}
                </Typography>
              </Grid>
            </Grid>

            <Grid
              container
              alignItems="center"
              wrap="nowrap"
              className={classes.row}
            >
              <Grid item>
                <AreaIcon className={classes.icon} />
              </Grid>
              <Grid item>
                <Typography className={classes.label}>
                  {t('AREA_LIKELY_HAVE_PSPS')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* Outages */}
      <Grid item>
        <Typography className={classes.sectionTitle}>{t('OUTAGES')}</Typography>
        <Paper className={classes.box}>
          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.row}
          >
            <Grid item>
              <PlannedOutageIcon className={classes.icon} />
            </Grid>
            <Grid item>
              <Typography className={classes.label}>
                {t('PLANNED_OUTAGE')}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.row}
          >
            <Grid item>
              <PlannedOutageClusterIcon
                text="50"
                size={40}
                className={classes.icon}
              />
            </Grid>
            <Grid item>
              <Typography className={classes.label}>
                {t('GROUPED_PLANNED_OUTAGE')}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.row}
          >
            <Grid item>
              <OutageMarkerIcon
                text="1"
                className={classes.icon}
                fillColor={colors.roseRed}
                size={32}
                strokeColor={colors.white}
                textColor={colors.white}
              />
            </Grid>
            <Grid item>
              <Typography className={classes.label}>
                {t('OUTAGE_WITH_CUSTOMER_AFFECTED')}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.row}
          >
            <Grid item>
              <OutageMarkerIcon
                text="50"
                variant="square"
                className={classes.icon}
                fillColor={colors.roseRed}
                size={32}
                strokeColor={colors.white}
                textColor={colors.white}
              />
            </Grid>
            <Grid item>
              <Typography className={classes.label}>
                {t('GROUPED_OUTAGE_EVENT')}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.row}
          >
            <Grid item>
              <OutageMarkerIcon
                text="99+"
                variant="square"
                className={classes.icon}
                fillColor={colors.roseRed}
                size={32}
                strokeColor={colors.white}
                textColor={colors.white}
              />
            </Grid>
            <Grid item>
              <Typography className={classes.label}>
                {t('GROUPED_OUTAGE_EVENT_WITH_99_PLUS')}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.row}
          >
            <Grid item>
              <OutageMarkerIcon
                text="1k+"
                variant="square"
                className={classes.icon}
                fillColor={colors.roseRed}
                size={32}
                strokeColor={colors.white}
                textColor={colors.white}
              />
            </Grid>
            <Grid item>
              <Typography className={classes.label}>
                {t('GROUPED_OUTAGE_EVENT_WITH_1k_PLUS')}
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            className={classes.row}
          >
            <Grid item>
              <AreaIcon color="#F26B1D80" className={classes.icon} />
            </Grid>
            <Grid item>
              <Typography className={classes.label}>
                {t('APPROXIMATE_OUTAGE_AREA')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
